/**
 * V1 → V2 redirect matching layer — pure matching logic (no Node APIs, unit-testable).
 *
 * This is the application's supported redirect mechanism. It matches an incoming
 * request path against a map of redirect rules and returns the matching rule (or
 * null). It runs both on SSR (server-side, via `redirects.loader.ts`) and
 * on SPA builds (via `redirect.shim.ts`), sharing this single matching
 * implementation so a path resolves identically in either environment.
 *
 * The redirect map is sourced from the Laravel Artisan command:
 *
 *     php artisan migrate:generate-redirects --format=json
 *
 * which produces `redirects.json` from the V1 database. This module never defines
 * redirects itself; it only matches incoming paths against that map. Custom
 * redirects managed in the admin panel are served by the API and merged into the
 * same map, so they flow through this same matching logic.
 *
 * Matching is EXACT-PATH FIRST and always takes precedence. A single, narrowly
 * scoped ID-based fallback then handles legacy event URLs. V1 (Django) routed
 * everything under `/event-date/<id>/…` and `/event-presale/<id>/…` — the base
 * page plus the `/forward/`, `/kyc/` and `/seat-selection/` sub-flows — by the
 * numeric id alone, IGNORING the slug. So when an exact match misses, any path of
 * the form `/event-date/<id>/…` or `/event-presale/<id>/…` for a known event id
 * resolves to that event's V2 page. This both preserves V1's slug tolerance
 * (renamed/non-canonical slugs still resolve) and lets the bulk `/forward/`,
 * `/kyc/`, `/seat-selection/` entries stay OUT of the generated map (~41k fewer
 * rows) while those URLs still redirect. `/event-date` and `/event-presale` are
 * not V2 routes, so this prefix fallback cannot shadow a live route. No other
 * wildcard/prefix matching exists; query strings are still stripped.
 */

/** A single redirect record as emitted by the Laravel generator's JSON format. */
export interface RedirectEntry {
  from: string;
  to: string;
  type?: string;
  status: number;
}

/** Pre-built lookup table keyed by normalised path → redirect entry. */
export type RedirectIndex = Map<string, RedirectEntry>;

/**
 * Live V2 routes that must NEVER be overridden by a generated/admin redirect.
 *
 * The V1 site reused some path names that V2 now serves as real, functional
 * routes. Because the generator emits a slash-tolerant key for every entry, a
 * legacy rule like `/forgot-password/ → /reset-password-request` would otherwise
 * shadow the live V2 `/forgot-password` route (the tokened set-password page
 * reached from the reset email), redirecting the user away — query string and
 * all — before Angular routing ever runs.
 *
 * Any path listed here is excluded from the index in BOTH trailing-slash
 * variants, so the V2 route always wins. Add a path here only when V2 owns it as
 * a real route AND a legacy redirect collides with it.
 */
const RESERVED_PATHS = ['/forgot-password'];

/**
 * Canonical legacy event URL — `/event-date/<id>/<slug>` or
 * `/event-presale/<id>/<slug>` (optional trailing slash, single slug segment).
 * Used when BUILDING the index to pick a real event entry to register the
 * id-only key against. {@link NON_EVENT_SLUGS} keeps a stray transactional entry
 * from being chosen as that canonical target.
 */
const EVENT_ID_PATTERN = /^\/(?:event-date|event-presale)\/(\d+)\/([^/]+)\/?$/;

/**
 * Prefix form used when MATCHING an incoming request: anything under
 * `/event-date/<id>/…` or `/event-presale/<id>/…` — base page or a `/forward/`,
 * `/kyc/`, `/seat-selection/` sub-flow — captures the id for the fallback lookup.
 */
const EVENT_ID_PREFIX = /^\/(?:event-date|event-presale)\/(\d+)(?:\/|$)/;

/**
 * Slugs that must never be picked as an event's canonical id target while
 * indexing (they are transactional sub-flows, not the event's real slug).
 */
const NON_EVENT_SLUGS = new Set(['forward', 'kyc', 'seat-selection']);

/** Synthetic index key for the id-only fallback. The `id:` prefix can never
 * collide with a real request path (which is always normalised to start with `/`).
 * Keyed by id alone: `/event-date/<id>` and `/event-presale/<id>` are the same
 * event and share a V2 destination. */
function eventIdKey(id: string): string {
  return `id:${id}`;
}

/**
 * Normalise a path for exact matching:
 *  - strips any query string / fragment (defence-in-depth; Express `req.path`
 *    already excludes these),
 *  - guarantees a single leading slash.
 *
 * Case is preserved and NO wildcard/regex behaviour is introduced.
 */
export function normalisePath(path: string): string {
  const withoutQuery = path.split('?')[0].split('#')[0];
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}

/**
 * Build the lookup table from the generated entries.
 *
 * The generator emits `from` values with a trailing slash (e.g.
 * `/blog/1/slug/`). Browsers may request the same URL with or without the
 * trailing slash, so each entry is indexed under both variants. This is
 * trailing-slash tolerance, NOT wildcard matching — every key is still an
 * exact, fully-qualified path.
 *
 * Malformed entries (missing/empty `from`) are skipped. A 410 (Gone) entry is
 * allowed to have an empty `to` because nothing is redirected to; any other
 * status with an empty `to` is treated as malformed and skipped. Earlier entries
 * win on key collisions, mirroring the top-to-bottom precedence of the
 * generated Nginx file.
 */
export function buildRedirectIndex(entries: RedirectEntry[]): RedirectIndex {
  const index: RedirectIndex = new Map();

  // Reserved live-V2 routes, expanded to both trailing-slash variants, so a key
  // that collides with one is never added to the index.
  const reserved = new Set(RESERVED_PATHS.flatMap((path) => keyVariants(path)));

  for (const entry of entries) {
    if (!entry || typeof entry.from !== 'string' || entry.from === '') {
      continue;
    }
    // 410 entries carry no destination; every other status requires a `to`.
    if (entry.status !== 410 && (typeof entry.to !== 'string' || entry.to === '')) {
      continue;
    }

    for (const key of keyVariants(entry.from)) {
      if (!reserved.has(key) && !index.has(key)) {
        index.set(key, entry);
      }
    }

    // Register an id-only key for each canonical event entry, so any legacy URL
    // for that id (non-canonical slug, /forward/, /kyc/, /seat-selection/) can
    // resolve to the event when the exact match misses — exactly as V1 did.
    // Earlier entries win, mirroring the exact-key precedence above.
    const event = EVENT_ID_PATTERN.exec(normalisePath(entry.from));
    if (event && !NON_EVENT_SLUGS.has(event[2].toLowerCase())) {
      const idKey = eventIdKey(event[1]);
      if (!index.has(idKey)) {
        index.set(idKey, entry);
      }
    }
  }

  return index;
}

/**
 * Resolve a redirect for the given request path.
 *
 * Exact path match wins (existing, supported behaviour). Only when that misses do
 * we apply the id-based fallback: any `/event-date/<id>/…` or `/event-presale/<id>/…`
 * URL (base, forward, kyc, seat-selection, or a non-canonical slug) resolves to the
 * event for that id, mirroring how V1 looked these up by id alone.
 */
export function matchRedirect(index: RedirectIndex, path: string): RedirectEntry | null {
  const normalised = normalisePath(path);

  const exact = index.get(normalised);
  if (exact) {
    return exact;
  }

  const event = EVENT_ID_PREFIX.exec(normalised);
  if (event) {
    return index.get(eventIdKey(event[1])) ?? null;
  }

  return null;
}

/** Returns the with- and without-trailing-slash variants of a normalised path. */
function keyVariants(from: string): string[] {
  const base = normalisePath(from);
  const variants = new Set<string>([base]);

  if (base.length > 1) {
    variants.add(base.endsWith('/') ? base.slice(0, -1) : `${base}/`);
  }

  return [...variants];
}
